import { Button } from '@/components'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import type React from 'react'
import { memo, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import Checkbox from '../Form/Checkbox'
import { Image } from '@/components/ui'

type DialogPopupType = {
  hookpoint:
    | 'LOGIN'
    | 'REGISTER'
    | 'HOME'
    | 'SPOTS'
    | 'PRESENTS'
    | 'AR_PHOTO_FRAME_SPOTDETAIL'
    | 'AR_PHOTO_FRAME_REWARDSDETAIL'
    | 'RESTORE'
  onClickFunc?: () => void
  dialogState?: boolean
  dialogSetState?: React.Dispatch<React.SetStateAction<boolean>>
  campaign: Api.CampaignResBody
}

export const DialogPopup = memo((props: DialogPopupType) => {
  const {
    hookpoint,
    onClickFunc,
    dialogState: externalDialogState,
    dialogSetState: externalDialogSetState,
    campaign,
  } = props

  if (externalDialogState === false) return <div>{''}</div>

  const { t } = useTranslation()
  const swiperRef = useRef(null)
  const dialogElem = useRef(null)
  const [internalDialogState, internalDialogSetState] = useState(true)
  const [isSwiperEnd, setIsSwiperEnd] = useState(false)
  const [swiperKey, setSwiperKey] = useState(0)
  const [displaySize, setDisplaySize] = useState({ width: 280, height: 330 })
  const { LOCAL_STORAGE_POPUP_HOOKPOINT_KEY } = useCampaignSlug()

  const { register } = useForm()

  const dialogState = externalDialogState !== undefined ? externalDialogState : internalDialogState
  const dialogSetState = externalDialogSetState !== undefined ? externalDialogSetState : internalDialogSetState
  const getCurrentPopup = (popup: Api.CampaignResBody['popup']) => {
    if (!popup) return null

    const popupStatus = localStorage.getItem(LOCAL_STORAGE_POPUP_HOOKPOINT_KEY(hookpoint))

    if (popupStatus === 'hide') return null

    // 特殊なケースの処理(スポットのARフォトと特典のARフォトのポップアップ「次回以降表示しない」機能を同期させないため)
    if (hookpoint === 'AR_PHOTO_FRAME_SPOTDETAIL' || hookpoint === 'AR_PHOTO_FRAME_REWARDSDETAIL') {
      return popup.find((item) => item.hookpoint === 'AR_PHOTO_FRAME')
    }

    return popup.find((item) => item.hookpoint === hookpoint)
  }

  const currentPopup = getCurrentPopup(campaign?.popup)

  useEffect(() => {
    if (!campaign) return
    if (!currentPopup && dialogState && typeof onClickFunc === 'function') {
      // 次回以降表示しないかつ関数だけ実行するパターン
      onClickFunc()
      return
    }
  }, [campaign, currentPopup])

  useEffect(() => {
    if (swiperRef.current) {
      setSwiperKey((prevKey) => prevKey + 1)

      if (currentPopup?.images.length === 1) {
        setIsSwiperEnd(true)
      }
    }
  }, [currentPopup])

  useEffect(() => {
    return () => {
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.destroy(true, true)
        swiperRef.current.swiper = null
      }
    }
  }, [])

  useEffect(() => {
    if (dialogElem.current) {
      if (dialogState) {
        disableBodyScroll(dialogElem.current)
      } else {
        enableBodyScroll(dialogElem.current)
      }
    }
  }, [dialogState])

  const CloseButton = () => {
    enableBodyScroll(dialogElem.current)
    dialogSetState(false)

    const checkbox = document.getElementById(`hidePopup-${hookpoint}`) as HTMLInputElement
    if (currentPopup && checkbox.checked) {
      localStorage.setItem(LOCAL_STORAGE_POPUP_HOOKPOINT_KEY(hookpoint), 'hide')
    }
  }

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext()
    }
  }

  const onClickButton = () => {
    if (isSwiperEnd) {
      CloseButton()
      if (typeof onClickFunc === 'function') {
        onClickFunc()
      }
    } else {
      goNext()
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const height = window.innerHeight
      // ダイアログの大きさを考慮した比率
      const dialogHeight = Math.min(height * 0.9, 600)
      const imageHeightInDialog = Math.min(dialogHeight * 0.6, 330)
      const width = Math.floor((imageHeightInDialog * 28) / 33)
      setDisplaySize({ width, height: imageHeightInDialog })
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  if (!currentPopup) return <div>{''}</div>
  return (
    <div className="c-dialog c-dialog--popup" id="my-dialog" aria-hidden={!dialogState}>
      <div className="c-dialog-overlay" onClick={() => CloseButton()} />
      <div className="c-dialog-wrap">
        <div role="dialog" className="c-dialog-content--popup" aria-labelledby="dialog" ref={dialogElem}>
          <div style={{ height: displaySize.height }}>
            <div className={'relative'}>
              <Swiper
                modules={[Pagination, Virtual]}
                speed={1000}
                loop={false}
                autoHeight={true}
                spaceBetween={30}
                slidesPerView={1}
                centeredSlides={true}
                allowTouchMove={false}
                ref={swiperRef}
                onSlideChange={(swiper) => setIsSwiperEnd(swiper.isEnd)}
                pagination={{
                  el: `#pagination-${hookpoint}`,
                  type: 'bullets',
                  clickable: true,
                }}
                virtual
              >
                {currentPopup?.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <div className={'flex justify-center w-full'} style={{ height: displaySize.height }}>
                      {image.url ? (
                        <a
                          href={image.url}
                          target={'_blank'}
                          className="flex items-center justify-center w-full"
                          rel="noreferrer"
                        >
                          <Image src={image.image} aspectRatio={'28/33'} />
                        </a>
                      ) : (
                        <Image src={image.image} aspectRatio={'28/33'} />
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
          <div
            className={`flex items-center justify-center ${
              isSwiperEnd && hookpoint !== 'RESTORE' ? '' : 'pointer-events-none opacity-0'
            }`}
          >
            <Checkbox
              htmlfor={`hidePopup-${hookpoint}`}
              register={register}
              formValueSplit={[t('common.popup.hide')]}
            />
          </div>
          <div className="mt-auto">
            <div id={`pagination-${hookpoint}`} className="flex justify-center space-x-1.75 mb-8" />
            <Button text={isSwiperEnd ? t('common.popup.finish') : t('common.popup.next')} onClick={onClickButton} />
          </div>
        </div>
      </div>
    </div>
  )
})
